import React, { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
    amountInDecimal,
    BigText,
    CancelNextButton,
    FinishPageType,
    FlowPage,
    FlowPageProps,
    Highlight,
    NormalText,
    SmallestText,
    SmallText,
} from "../cancelFlow";
import {
    useCurrentStripePlan,
    useStripeSubscription,
    useStripeUpgradePlan,
} from "../../../../../components/hooks";
import { getLocale } from "../../../../../utils/localeUtils";
import { formatForCurrency } from "../../../../../utils/CurrencySymbol";
import { IonButton, IonLoading, IonText } from "@ionic/react";
import * as stripeService from "../../../../../services/StripeService";
import {
    asPlanType,
    planDisplayName,
} from "../../../../../services/StripeService";
import { trackEvent } from "../../../../../services/analytics/analyticsService";

export const SwitchToAnnualForSavingPage: FC<FlowPageProps> = ({
    next,
    finish,
    reason,
}) => {
    const { t } = useTranslation();
    const sub = useStripeSubscription();
    const currentPlan = useCurrentStripePlan();
    const upgradePlan = useStripeUpgradePlan(
        "year",
        currentPlan?.metadata?.plan_features || "",
        true
    );
    const currency = currentPlan?.currency || "USD";

    const currentPlanAmount = amountInDecimal(
        currentPlan?.amount * 12,
        currency
    );
    const upgradePlanAmount = amountInDecimal(upgradePlan?.amount, currency);

    const savings = currentPlanAmount - upgradePlanAmount;
    const savingsPercentage = (savings / currentPlanAmount) * 100;
    const savingsAmount = savings;
    const locale = getLocale();

    const onClick = async () => {
        try {
            if (sub) {
                const planType = asPlanType(sub.planFeatures);
                await stripeService.updatePlan("year", planType);
                trackEvent("CancellationOutcome", {
                    reason: reason,
                    outcome: "switched-to-annual",
                });
            }
        } catch (e) {
            console.error(e);
        }
        return FinishPageType.OfferSwitch;
    };
    return (
        <FlowPage className={"switch-to-annual"}>
            <div className={"content"}>
                <NormalText>
                    {t("cancellation.screens.savings.title")}
                </NormalText>
                <BigText>
                    <Trans
                        i18nKey={"cancellation.screens.savings.text"}
                        values={{ savings: savingsPercentage.toFixed(0) }}
                        components={{ b: <Highlight /> }}
                    />
                </BigText>
                <NormalText>
                    <Highlight>
                        {t("cancellation.screens.savings.text2", {
                            price: formatForCurrency(
                                upgradePlanAmount,
                                currency,
                                locale
                            ),
                        })}
                    </Highlight>
                </NormalText>
                <SmallestText>
                    {t("cancellation.screens.savings.text3", {
                        price: formatForCurrency(
                            currentPlanAmount,
                            currency,
                            locale
                        ),
                    })}
                </SmallestText>
            </div>
            <SmallText>
                {t("cancellation.screens.savings.footer", {
                    newPlan: planDisplayName(upgradePlan),
                    savings: formatForCurrency(savingsAmount, currency, locale),
                })}
            </SmallText>
            <CancelNextButton next={next}>
                {t("cancellation.screens.savings.noButton")}
            </CancelNextButton>
            <IonButton onClick={() => finish && finish(onClick)}>
                {t("general.yes")}
            </IonButton>
        </FlowPage>
    );
};
